<template>
    <div class="dashboard-container">
        <div class="dashboard-cards">
            <!-- Card 1: System Status -->
            <v-card
                class="dashboard-card"
                style="
                    background-color: var(--zsh-messages-panel-bg);
                    color: var(--zsh-messages-text);
                    border: 1px solid var(--zsh-messages-border);
                "
            >
                <v-card-title
                    style="
                        background-color: var(--zsh-primary);
                        color: white;
                        padding: 12px 16px;
                        border-radius: 4px 4px 0 0;
                    "
                    >{{ $t('dashboard.zsh_messaging_system_status') }}</v-card-title
                >
                <v-card-text class="pa-3">
                    <!-- API Key info -->
                    <div class="d-flex align-center py-1 mb-1">
                        <v-icon color="grey" size="20" class="mr-2">mdi-key-variant</v-icon>
                        <span class="text-body-2 font-weight-medium mr-2">API Key</span>
                        <code class="text-caption text-grey">{{ intercomKeyPreview }}</code>
                    </div>
                    <v-divider class="mb-1" />

                    <!-- Row 1: Messaging System status -->
                    <div class="d-flex align-center py-1">
                        <v-progress-circular
                            v-if="isLoadingZsh"
                            indeterminate
                            color="var(--zsh-primary)"
                            size="20"
                            width="2"
                            class="mr-2"
                        />
                        <v-icon v-else-if="isZshCompanyCreated === true" color="green" size="20" class="mr-2"
                            >mdi-check-circle</v-icon
                        >
                        <v-icon v-else-if="zshError" color="red" size="20" class="mr-2">mdi-close-circle</v-icon>
                        <v-icon v-else color="orange" size="20" class="mr-2">mdi-alert-circle</v-icon>
                        <span class="text-body-2 font-weight-medium mr-2">{{ $t('messages.messenger') }}</span>
                        <span v-if="isLoadingZsh" class="text-caption text-grey">{{
                            $t('dashboard.checking_messaging_status')
                        }}</span>
                        <span v-else-if="zshError" class="text-caption text-red">{{ zshError }}</span>
                        <span v-else-if="isZshCompanyCreated === true" class="text-caption text-green">{{
                            $t('dashboard.zsh_messaging_initialized')
                        }}</span>
                        <span v-else class="text-caption text-orange">{{
                            $t('dashboard.zsh_messaging_not_initialized')
                        }}</span>
                    </div>

                    <!-- Row 1.5: Internal Company ID -->
                    <div v-if="zshCompanyInternalId" class="d-flex align-center py-1">
                        <v-icon color="grey" size="20" class="mr-2">mdi-identifier</v-icon>
                        <span class="text-body-2 font-weight-medium mr-2">Company ID</span>
                        <code class="text-caption text-grey" style="user-select: all;">{{ zshCompanyInternalId }}</code>
                    </div>

                    <!-- Row 2: Ticket Types status -->
                    <div class="d-flex align-center py-1">
                        <v-progress-circular
                            v-if="isCreatingTicketTypes || isLoadingTicketTypes || isUpdatingTicketTypes"
                            indeterminate
                            color="var(--zsh-primary)"
                            size="20"
                            width="2"
                            class="mr-2"
                        />
                        <v-icon
                            v-else-if="ticketTypes && ticketTypes.length > 0"
                            color="green"
                            size="20"
                            class="mr-2"
                            >mdi-check-circle</v-icon
                        >
                        <v-icon v-else-if="ticketTypesErr" color="red" size="20" class="mr-2"
                            >mdi-close-circle</v-icon
                        >
                        <v-icon v-else color="orange" size="20" class="mr-2">mdi-alert</v-icon>
                        <span class="text-body-2 font-weight-medium mr-2">Ticket Types</span>
                        <span
                            v-if="isCreatingTicketTypes || isLoadingTicketTypes || isUpdatingTicketTypes"
                            class="text-caption text-grey"
                        >
                            {{
                                isCreatingTicketTypes
                                    ? $t('dashboard.creating_ticket_types')
                                    : isUpdatingTicketTypes
                                      ? $t('dashboard.updating_ticket_types')
                                      : $t('dashboard.loading_ticket_types')
                            }}
                        </span>
                        <span v-else-if="ticketTypesErr" class="text-caption text-red">{{ ticketTypesErr }}</span>
                        <span v-else-if="ticketTypes && ticketTypes.length > 0" class="text-caption text-green">
                            {{ ticketTypes.length }} {{ $t('dashboard.required_ticket_types_initialized') }}
                        </span>
                        <span v-else class="text-caption text-orange">{{ $t('dashboard.no_ticket_types_found') }}</span>
                    </div>

                    <!-- Initialize button -->
                    <div v-if="isZshCompanyCreated === false && !isLoadingZsh" class="mt-2">
                        <v-btn color="teal" size="small" :disabled="isLoadingZsh" @click="handleCreateCompany">
                            <v-icon start>mdi-plus</v-icon>
                            {{ $t('dashboard.initialize') }}
                        </v-btn>
                    </div>

                    <!-- Reset button -->
                    <div class="mt-3 d-flex justify-end">
                        <v-btn
                            color="red-lighten-1"
                            variant="text"
                            size="x-small"
                            :loading="isResetting"
                            @click="showResetConfirm = true"
                        >
                            <v-icon start size="small">mdi-delete-sweep</v-icon>
                            {{ $t('dashboard.reset_messaging') }}
                        </v-btn>
                    </div>

                    <!-- Reset confirmation dialog -->
                    <v-dialog v-model="showResetConfirm" max-width="420" persistent>
                        <v-card>
                            <v-card-title class="text-h6">{{ $t('dashboard.reset_confirm_title') }}</v-card-title>
                            <v-card-text>{{ $t('dashboard.reset_confirm_text') }}</v-card-text>
                            <v-card-actions>
                                <v-spacer />
                                <v-btn variant="text" @click="showResetConfirm = false">{{ $t('messages.cancel') }}</v-btn>
                                <v-btn color="red" variant="flat" :loading="isResetting" @click="handleReset">
                                    {{ $t('dashboard.reset_confirm_button') }}
                                </v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-dialog>
                </v-card-text>
            </v-card>

            <!-- Card 2: Supplier Sync -->
            <v-card
                v-if="isTO1User"
                class="dashboard-card"
                style="
                    background-color: var(--zsh-messages-panel-bg);
                    color: var(--zsh-messages-text);
                    border: 1px solid var(--zsh-messages-border);
                "
            >
                <v-card-title
                    style="
                        background-color: var(--zsh-primary);
                        color: white;
                        padding: 12px 16px;
                        border-radius: 4px 4px 0 0;
                    "
                    >{{ $t('dashboard.supplier_companies_sync') }}</v-card-title
                >
                <v-card-text class="pa-3">
                    <!-- Loading state -->
                    <div v-if="isSuppliersLoading" class="d-flex align-center mt-2">
                        <v-progress-circular indeterminate color="var(--zsh-primary)" class="mr-3" size="24" />
                        <span>{{ $t('dashboard.checking_supplier_companies') }}</span>
                    </div>

                    <!-- Error state -->
                    <div v-else-if="supplierErr" class="mt-2">
                        <v-alert type="error" density="compact">{{ supplierErr }}</v-alert>
                    </div>

                    <!-- Main content -->
                    <div v-else>
                        <!-- Summary chips + Sync button -->
                        <div class="d-flex flex-wrap align-center ga-2 mb-3">
                            <v-chip color="green" variant="tonal" size="small">
                                {{ syncedCount }} {{ $t('dashboard.synced') }}
                            </v-chip>
                            <v-chip v-if="pendingCount > 0" color="orange" variant="tonal" size="small">
                                {{ pendingCount }} {{ $t('dashboard.pending') }}
                            </v-chip>
                            <v-chip v-if="errorCount > 0" color="red" variant="tonal" size="small">
                                {{ errorCount }} {{ $t('dashboard.errors') }}
                            </v-chip>
                            <v-chip v-if="incompleteCount > 0" color="amber" variant="tonal" size="small">
                                {{ incompleteCount }} {{ $t('dashboard.incomplete') }}
                            </v-chip>
                            <v-spacer />
                            <v-btn
                                color="teal"
                                size="small"
                                :disabled="(pendingCount === 0 && errorCount === 0 && incompleteCount === 0) || isSyncingSuppliers"
                                :loading="isSyncingSuppliers"
                                @click="handleSyncAll"
                            >
                                <v-icon start>mdi-sync</v-icon>
                                {{ $t('dashboard.sync_all') }}
                            </v-btn>
                        </div>

                        <!-- Sync progress -->
                        <div v-if="isSyncingSuppliers" class="mb-3">
                            <v-progress-linear :model-value="syncProgress" color="teal" height="20" striped>
                                <template v-slot="{value}">
                                    <span class="text-caption">{{ Math.ceil(value) }}%</span>
                                </template>
                            </v-progress-linear>
                            <div class="text-caption mt-1">
                                {{
                                    $t('dashboard.syncing', {
                                        current: currentSyncItem,
                                        total: totalSyncItems,
                                    })
                                }}
                            </div>
                        </div>

                        <!-- Data table -->
                        <v-data-table
                            :headers="tableHeaders"
                            :items="supplierSyncStates"
                            :search="tableSearch"
                            density="compact"
                            :items-per-page="15"
                            class="supplier-sync-table"
                        >
                            <template v-slot:top>
                                <v-text-field
                                    v-model="tableSearch"
                                    prepend-inner-icon="mdi-magnify"
                                    :label="$t('dashboard.table_company')"
                                    density="compact"
                                    variant="outlined"
                                    hide-details
                                    class="mb-2"
                                    style="max-width: 300px"
                                />
                            </template>

                            <template v-slot:item.name="{item}">
                                <nuxt-link
                                    :to="{name: 'companySupplier', params: {id: item.id}}"
                                    class="company-link"
                                    no-prefetch
                                >
                                    {{ item.name }}
                                </nuxt-link>
                            </template>

                            <template v-slot:item.intercomStatus="{item}">
                                <v-chip
                                    :color="
                                        item.intercomStatus === 'synced'
                                            ? 'green'
                                            : item.intercomStatus === 'error'
                                              ? 'red'
                                              : 'orange'
                                    "
                                    variant="tonal"
                                    size="x-small"
                                >
                                    {{ $t('dashboard.' + item.intercomStatus) }}
                                </v-chip>
                            </template>

                            <template v-slot:item.users="{item}">
                                <span v-if="item.intercomStatus === 'synced'">
                                    {{ item.usersAssociated }}/{{ item.usersTotal }}
                                </span>
                                <span v-else class="text-grey">-</span>
                            </template>

                            <template v-slot:item.issues="{item}">
                                <span v-if="item.error" class="text-red text-caption">{{ item.error }}</span>
                                <div v-else-if="item.usersMissingEmail > 0">
                                    <v-tooltip location="top">
                                        <template #activator="{props}">
                                            <span v-bind="props" class="text-orange text-caption" style="cursor: help;">
                                                {{ $t('dashboard.missing_email', {count: item.usersMissingEmail}) }}
                                            </span>
                                        </template>
                                        <div class="text-caption">
                                            <div v-for="name in (item.usersWithoutEmail || []).slice(0, 10)" :key="name">{{ name }}</div>
                                            <div v-if="(item.usersWithoutEmail || []).length > 10">...and {{ item.usersWithoutEmail.length - 10 }} more</div>
                                        </div>
                                    </v-tooltip>
                                </div>
                                <span v-else class="text-grey text-caption">{{ $t('dashboard.no_issues') }}</span>
                            </template>
                        </v-data-table>
                    </div>
                </v-card-text>
            </v-card>
        </div>
    </div>
</template>

<script lang="ts">
    import {defineComponent, onMounted, ref, watch} from 'vue'
    import {useTicketTypes} from '../../composables/useTicketTypes'
    import {useMessagingSystem} from '../../composables/useMessagingSystem'
    import {authStore} from '@/composables/store-accessor'
    import {useI18n} from 'vue-i18n'

    export default defineComponent({
        name: 'DashboardTab',
        setup() {
            const {t} = useI18n()

            const {
                ticketTypes,
                isLoadingTicketTypes,
                ticketTypesError: ticketTypesErr,
                isCreatingTicketTypes,
                isUpdatingTicketTypes,
                ensureTicketTypesExist,
            } = useTicketTypes()

            const {
                error: zshError,
                isLoading: isLoadingZsh,
                isZshCompanyCreated,
                zshCompanyInternalId,
                isSuppliersLoading,
                supplierError: supplierErr,
                isSyncingSuppliers,
                syncProgress,
                currentSyncItem,
                totalSyncItems,
                isTO1User,
                supplierSyncStates,
                syncedCount,
                pendingCount,
                errorCount,
                incompleteCount,
                checkCompanyExists,
                createCompany,
                loadSuppliersAndCheckIntercom,
                syncAll,
                ensureTO1UserAssociated,
                loadAndCacheCompanyId,
            } = useMessagingSystem()

            const runtimeConfig = useRuntimeConfig()
            const intercomKeyPreview = (() => {
                const key = runtimeConfig.public?.intercomApi as string || ''
                if (!key || key.length < 16) return key ? '***' : 'Not configured'
                return `${key.substring(0, 8)}...${key.substring(key.length - 4)}`
            })()

            const tableSearch = ref('')

            const tableHeaders = [
                {title: t('dashboard.table_company'), key: 'name', sortable: true},
                {title: t('dashboard.table_status'), key: 'intercomStatus', sortable: true, width: '100px'},
                {title: t('dashboard.table_users'), key: 'users', sortable: false, width: '80px'},
                {title: t('dashboard.table_issues'), key: 'issues', sortable: false},
            ]

            // Store the current user's contact ID in localStorage
            const storeCurrentUserContactId = async () => {
                try {
                    const token = useCookie('token').value
                    if (!token) return

                    const userEmail = authStore.person?.email
                    if (!userEmail) return

                    const response = await fetch('/proxy/intercom/contacts/search', {
                        method: 'POST',
                        headers: {
                            Accept: 'application/json',
                            'Content-Type': 'application/json',
                            Token: token,
                        },
                        body: JSON.stringify({
                            query: {field: 'email', operator: '=', value: userEmail},
                        }),
                    })

                    if (response.ok) {
                        const data = await response.json()
                        if (data?.data?.length > 0) {
                            const contactId = data.data[0].id
                            localStorage.setItem('currentUserContactId', contactId)
                            // Persist to MongoDB (fire-and-forget)
                            fetch('/proxy/messaging/config', {
                                method: 'POST',
                                headers: {'Content-Type': 'application/json', Token: token},
                                body: JSON.stringify({key: 'currentUserContactId', value: contactId}),
                            }).catch(() => {})
                        }
                    }
                } catch (error) {
                    console.error('Error storing current user contact ID:', error)
                }
            }

            const initializeDashboard = async () => {
                if (isTO1User.value) {
                    await checkCompanyExists()

                    if (isLoadingZsh.value) {
                        await new Promise<void>(resolve => {
                            const unwatch = watch(isLoadingZsh, newValue => {
                                if (!newValue) {
                                    unwatch()
                                    resolve()
                                }
                            })
                        })
                    }

                    if (isZshCompanyCreated.value !== false) {
                        await ensureTO1UserAssociated()

                        try {
                            await loadAndCacheCompanyId()
                        } catch (error) {
                            console.warn('Failed to load ZSH company ID, continuing without it:', error)
                        }

                        const userEmail = authStore.person?.email || ''
                        localStorage.setItem('userEmail', userEmail)
                        // Persist userEmail to MongoDB (fire-and-forget)
                        if (userEmail) {
                            const token = useCookie('token').value
                            if (token) {
                                fetch('/proxy/messaging/config', {
                                    method: 'POST',
                                    headers: {'Content-Type': 'application/json', Token: token},
                                    body: JSON.stringify({key: 'userEmail', value: userEmail}),
                                }).catch(() => {})
                            }
                        }
                        await storeCurrentUserContactId()
                    }
                    await ensureTicketTypesExist()
                    await loadSuppliersAndCheckIntercom()
                }
            }

            onMounted(() => {
                initializeDashboard()
            })

            const handleCreateCompany = async () => {
                await createCompany()
                if (isZshCompanyCreated.value === true && isTO1User.value) {
                    await ensureTO1UserAssociated()
                    await loadAndCacheCompanyId()
                    await ensureTicketTypesExist()
                    await loadSuppliersAndCheckIntercom()
                }
            }

            const handleSyncAll = async () => {
                await syncAll()
            }

            const showResetConfirm = ref(false)
            const isResetting = ref(false)

            const handleReset = async () => {
                isResetting.value = true
                try {
                    const token = useCookie('token').value
                    if (!token) throw new Error('No token')

                    // Clear MongoDB
                    await fetch('/proxy/messaging/reset', {
                        method: 'POST',
                        headers: {'Content-Type': 'application/json', Token: token},
                    })

                    // Clear all messaging-related localStorage keys
                    const keysToRemove: string[] = []
                    for (let i = 0; i < localStorage.length; i++) {
                        const key = localStorage.key(i)
                        if (key && (
                            key.startsWith('company_') ||
                            key.startsWith('targetCompanyAttrId_') ||
                            key === 'zeroStarHotelsCompanyId' ||
                            key === 'currentUserContactId' ||
                            key === 'userEmail' ||
                            key === 'questionsTicketTypeId' ||
                            key === 'defaultTicketTypeId' ||
                            key === 'supplierSyncStates'
                        )) {
                            keysToRemove.push(key)
                        }
                    }
                    keysToRemove.forEach(k => localStorage.removeItem(k))

                    showResetConfirm.value = false

                    // Reload the page to reinitialize
                    window.location.reload()
                } catch (error) {
                    console.error('Error resetting messaging data:', error)
                } finally {
                    isResetting.value = false
                }
            }

            return {
                // Ticket types
                ticketTypes,
                isLoadingTicketTypes,
                ticketTypesErr,
                isCreatingTicketTypes,
                isUpdatingTicketTypes,

                // Messaging system
                zshCompanyInternalId,
                zshError,
                isLoadingZsh,
                isZshCompanyCreated,
                isTO1User,
                isSuppliersLoading,
                supplierErr,
                isSyncingSuppliers,
                syncProgress,
                currentSyncItem,
                totalSyncItems,
                supplierSyncStates,
                syncedCount,
                pendingCount,
                errorCount,
                incompleteCount,

                // Table
                intercomKeyPreview,
                tableSearch,
                tableHeaders,

                // Actions
                handleCreateCompany,
                handleSyncAll,
                showResetConfirm,
                isResetting,
                handleReset,
            }
        },
    })
</script>

<style scoped>
    @import '../../assets/styles/messages-page.css';

    .company-link {
        text-decoration: none;
        color: var(--zsh-primary);
    }
    .company-link:hover {
        text-decoration: underline;
    }

    .supplier-sync-table {
        font-size: 0.85rem;
    }
</style>
